import React, { useState } from "react";
import { Plus, X, Trash2, Edit2, Save } from "lucide-react";
import { api } from "../../lib/api";
import { ImageUpload } from "../../components/ImageUpload";

const blank = {
  slug: "",
  title: "",
  title_pt: "",
  excerpt: "",
  excerpt_pt: "",
  content: "",
  content_pt: "",
  image: "",
  date: new Date().toISOString().slice(0, 10),
  author: "Kultopia Editorial",
  tag: "NEWS",
};

export const AdminBlogTab = ({ posts, refresh }) => {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(blank);
  const [editingSlug, setEditingSlug] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setForm(blank);
    setEditingSlug(null);
    setShow(false);
    setError("");
  };

  const startEdit = (p) => {
    setEditingSlug(p.slug);
    setForm({
      slug: p.slug,
      title: p.title || "",
      title_pt: p.title_pt || "",
      excerpt: p.excerpt || "",
      excerpt_pt: p.excerpt_pt || "",
      content: p.content || "",
      content_pt: p.content_pt || "",
      image: p.image || "",
      date: p.date || "",
      author: p.author || "",
      tag: p.tag || "NEWS",
    });
    setShow(true);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      if (editingSlug) await api.patch(`/admin/blog/${editingSlug}`, form);
      else await api.post("/admin/blog", form);
      reset();
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const del = async (slug) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/admin/blog/${slug}`);
      await refresh();
    } catch {
      // ignore
    }
  };

  return (
    <section data-testid="admin-blog-section">
      <div className="flex items-center justify-between mb-4">
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ JOURNAL</p>
        <button onClick={() => (show ? reset() : setShow(true))} className="bg-kult-pink text-black px-5 py-2 font-display text-lg hover:bg-kult-yellow inline-flex items-center gap-1" data-testid="admin-new-blog-toggle">
          {show ? <X size={16} /> : <Plus size={16} />} {show ? "CLOSE" : "NEW POST"}
        </button>
      </div>

      {show && (
        <form onSubmit={submit} className="border-2 border-kult-pink p-6 bg-kult-black2 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-new-blog-form">
          <h3 className="md:col-span-2 font-display text-2xl">{editingSlug ? "EDIT POST" : "NEW POST"}</h3>
          {!editingSlug && <Field label="SLUG" required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} testId="new-blog-slug" />}
          <Field label="DATE" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} testId="new-blog-date" />
          <Field label="TITLE (EN)" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} testId="new-blog-title" />
          <Field label="TITLE (PT)" value={form.title_pt} onChange={(e) => setForm({ ...form, title_pt: e.target.value })} testId="new-blog-title-pt" />
          <Field label="AUTHOR" required value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} testId="new-blog-author" />
          <Field label="TAG (e.g. EVENTS, INTERVIEW)" required value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} testId="new-blog-tag" />
          <Textarea label="EXCERPT (EN)" required value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="md:col-span-2" testId="new-blog-excerpt" />
          <Textarea label="EXCERPT (PT)" value={form.excerpt_pt} onChange={(e) => setForm({ ...form, excerpt_pt: e.target.value })} className="md:col-span-2" testId="new-blog-excerpt-pt" />
          <Textarea label="CONTENT (EN)" required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="md:col-span-2" rows={6} testId="new-blog-content" />
          <Textarea label="CONTENT (PT)" value={form.content_pt} onChange={(e) => setForm({ ...form, content_pt: e.target.value })} className="md:col-span-2" rows={6} testId="new-blog-content-pt" />
          <div className="md:col-span-2">
            <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted mb-2">COVER IMAGE</p>
            <ImageUpload value={form.image ? [form.image] : []} onChange={(imgs) => setForm({ ...form, image: imgs[0] || "" })} max={1} testIdPrefix="blog-image" recommendedSize="1600×900 (16:9 landscape)" />
          </div>
          {error && <p className="md:col-span-2 font-mono text-xs text-kult-pink">{error}</p>}
          <button type="submit" disabled={submitting} className="md:col-span-2 bg-kult-pink text-black font-display text-xl py-3 hover:bg-kult-yellow disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="new-blog-submit">
            <Save size={18} /> {submitting ? "..." : editingSlug ? "SAVE CHANGES" : "PUBLISH"}
          </button>
        </form>
      )}

      <div className="space-y-3" data-testid="admin-blog-list">
        {posts.map((p) => (
          <article key={p.id} className="border border-kult-border p-4 hover:border-kult-pink flex gap-4 items-center" data-testid={`admin-blog-${p.slug}`}>
            {p.image && <img src={p.image} alt="" className="w-20 h-20 object-cover grayscale" />}
            <div className="flex-1 min-w-0">
              <p className="font-mono text-[10px] tracking-[0.25em] text-kult-yellow">{p.tag} · {p.date}</p>
              <h3 className="font-display text-2xl leading-tight truncate">{p.title}</h3>
              <p className="font-mono text-[11px] text-kult-muted mt-1 truncate">{p.excerpt}</p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => startEdit(p)} className="p-2 border border-kult-border hover:border-kult-pink hover:text-kult-pink" data-testid={`admin-blog-edit-${p.slug}`}>
                <Edit2 size={14} />
              </button>
              <button onClick={() => del(p.slug)} className="p-2 border border-kult-border hover:border-kult-pink hover:text-kult-pink" data-testid={`admin-blog-delete-${p.slug}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

function Field({ label, testId, className = "", ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">{label}</span>
      <input {...props} data-testid={testId} className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none" />
    </label>
  );
}
function Textarea({ label, testId, className = "", rows = 3, ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">{label}</span>
      <textarea rows={rows} {...props} data-testid={testId} className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none" />
    </label>
  );
}
